/**
 * What a tool call is called while it runs, in the user's words. Shared by
 * the docked chat's pill and the Conversations pill so "task_list" never
 * shows up raw on one surface and as "Listing tasks" on the other.
 *
 * Three tiers: an exact label for the tools people see most, a verb map for
 * the `<noun>_<verb>` family, and a humanized fallback. When the call's args
 * carry a name or query, the label says what is being looked up.
 */

const EXACT_LABELS: Record<string, string> = {
  search: 'Searching your workspace',
  find_tools: 'Finding the right tool',
  whoami: 'Checking who you are',
  my_overview: 'Pulling your overview',
  my_streaks: 'Checking your streak and goals',
  goal_set: 'Setting your weekly goal',
  my_attention: 'Checking what needs you',
  my_tasks: 'Checking your tasks',
  onboarding_status: 'Checking your setup',
  ticket_attention_summary: 'Checking your tickets',
  member_list: 'Looking up your team',
  member_get: 'Looking up a teammate',
  entity_workspace: 'Opening the client workspace',
  project_workspace: 'Opening the project workspace',
  project_statuses_get: 'Checking the project statuses',
  timeentry_start_timer: 'Starting the timer',
  timeentry_stop_timer: 'Stopping the timer',
  timeentry_pause_timer: 'Pausing the timer',
  timeentry_resume_timer: 'Resuming the timer',
  timeentry_active_timer: 'Checking the timer',
  meeting_recording_start: 'Starting the recording',
  meeting_recording_stop: 'Stopping the recording',
  open_in_app: 'Opening it in Scrambl',
  sidebar_pin: 'Pinning to your sidebar',
  sidebar_unpin: 'Unpinning from your sidebar',
  sidebar_pins_list: 'Checking your pins',
  import_start: 'Starting the import',
  import_status: 'Checking the import',
  import_fields_list: 'Reading the import fields',
  import_cancel: 'Canceling the import',
  memory_save: 'Remembering that',
  memory_list: 'Checking what I remember',
  memory_delete: 'Forgetting that',
  learned_preference_save: 'Noting your preference',
  learned_preference_list: 'Checking your preferences',
  kb_article_search: 'Searching the knowledge base',
  invoice_record_payment: 'Recording the payment',
  timesheet_approvals_approve: 'Approving the time entries',
  timesheet_approvals_reject: 'Rejecting the time entries',
  timesheet_approvals_list: 'Checking the approvals',
}

const VERB_LABELS: Record<string, (noun: string) => string> = {
  get: noun => `Looking up the ${noun}`,
  list: noun => `Checking your ${plural(noun)}`,
  create: noun => `Creating the ${noun}`,
  update: noun => `Updating the ${noun}`,
  delete: noun => `Deleting the ${noun}`,
  assign: noun => `Assigning the ${noun}`,
  complete: noun => `Completing the ${noun}`,
  archive: noun => `Archiving the ${noun}`,
  unarchive: noun => `Restoring the ${noun}`,
  convert: noun => `Converting the ${noun}`,
  split: noun => `Splitting the ${noun}`,
  approve: noun => `Approving the ${noun}`,
  reject: noun => `Rejecting the ${noun}`,
  start: noun => `Starting the ${noun}`,
  stop: noun => `Stopping the ${noun}`,
  search: noun => `Searching ${plural(noun)}`,
  projects: noun => `Checking the ${noun}'s projects`,
}

const NOUNS: Record<string, string> = {
  entity: 'client',
  timeentry: 'time entry',
  taskgroup: 'task group',
  kb_article: 'article',
  custom_collection: 'collection',
  quote_product: 'product',
  booking_link: 'booking link',
  marketing_campaign: 'campaign',
}

/**
 * Pluralizes the last word of a noun phrase. Some tool names already carry a
 * plural noun ("portal_tasks_list", "ticket_statuses_list"), so a word that
 * ends in a plain "s" is left alone; "ss", "us" and "is" endings ("address",
 * "status", "analysis") are singular and still take "es".
 */
function plural(noun: string): string {
  if (/(?:ss|us|x|ch|sh)$/.test(noun)) return `${noun}es`
  if (/is$/.test(noun)) return `${noun.slice(0, -2)}es`
  if (/s$/.test(noun)) return noun
  if (/[^aeiou]y$/.test(noun)) return `${noun.slice(0, -1)}ies`
  return `${noun}s`
}

/** The name a call is about, when its args carry one worth showing. */
export function toolCallSubject(args?: unknown): string | null {
  if (!args || typeof args !== 'object') return null
  const record = args as Record<string, unknown>
  for (const key of ['name', 'query', 'title', 'subject', 'invoiceNumber']) {
    const value = record[key]
    if (typeof value === 'string') {
      const trimmed = value.trim()
      // Ids are never a subject; a query string that is really a filter
      // expression is not one either.
      if (
        trimmed &&
        trimmed.length <= 60 &&
        !/^[0-9a-f-]{32,}$/i.test(trimmed) &&
        !/[:=]/.test(trimmed)
      ) {
        return trimmed
      }
    }
  }
  return null
}

/**
 * "Checking your tasks", "Creating the task", "Looking up Acme". Never the
 * raw tool name.
 */
export function toolCallLabel(toolName: string, args?: unknown): string {
  const subject = toolCallSubject(args)
  if (subject) {
    const lastSegment = toolName.split('_').pop() ?? ''
    if (
      toolName === 'search' ||
      lastSegment === 'list' ||
      lastSegment === 'get'
    ) {
      return `Looking up ${subject}`
    }
    if (lastSegment === 'create') return `Creating "${subject}"`
  }
  const exact = EXACT_LABELS[toolName]
  if (exact) return exact
  const parts = toolName.split('_')
  const verb = parts[parts.length - 1] ?? ''
  const nounKey = parts.slice(0, -1).join('_')
  const noun = NOUNS[nounKey] ?? nounKey.replace(/_/g, ' ')
  const build = VERB_LABELS[verb]
  if (build && noun) return build(noun)
  const words = toolName.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * "Step 2 of 3: looking up the client" while one call of a batched reply
 * runs; the plain label when the reply asked for a single call or the
 * stream carried no count (P5-3). The label is a sentence fragment, so its
 * first letter drops to lowercase after the colon.
 */
export function stepLabel(
  label: string,
  step?: number,
  stepCount?: number,
): string {
  if (!step || !stepCount || stepCount < 2) return label
  return `Step ${step} of ${stepCount}: ${label.charAt(0).toLowerCase()}${label.slice(1)}`
}
